import type { DentistItemSlot, PatientInterviewState } from "../types";

/** 課題文のタスク（面接中に取得する指示書 tasks の必要部分）。 */
export interface ProgressSourceTask {
  no?: number;
  title: string;
  /** "C3:Lv1" や "C3:Lv2_frequency" の形。項目の columnKey か level と照合する。 */
  source_cells?: string[];
  related_conditions?: string[];
}

export interface TaskProgress {
  /** 課題文のタスク番号。コミュニケーションは 0。 */
  no: number;
  title: string;
  got: number;
  total: number;
  done: boolean;
}

export interface InterviewProgress {
  /** tasks: タスクごとに数えた。overall: タスクと項目を結べず全体だけ数えた。 */
  mode: "tasks" | "overall";
  /** mode=tasks のとき、課題文のタスクと同じ順番・同じ数で並ぶ。数えられないタスクは total=0。 */
  tasks: TaskProgress[];
  /** 挨拶・自己紹介などのコミュニケーション（C0）。採点の対象なのでゲージにも含める。 */
  communication: TaskProgress | null;
  got: number;
  total: number;
  doneTasks: number;
  /** 数えられるタスクの数（星の数）。 */
  countableTasks: number;
  complete: boolean;
  /** 目安の割合（PROGRESS_TARGET_RATIO）まで聞けた。報告に進んでよい目安。 */
  reachedTarget: boolean;
}

/** 報告に進んでよい目安。患者面接の点数はほぼ「聞けた項目 ÷ 対象の項目」なので8割を目安にする。 */
export const PROGRESS_TARGET_RATIO = 0.8;

const COMMUNICATION_LEVELS: Array<[string, string]> = [
  ["Lv1", "挨拶"],
  ["Lv2", "自己紹介"],
  ["Lv3", "氏名確認"],
  ["Lv4", "生年月日確認"],
  ["Lv5", "同意取得"],
  ["Lv6", "共感"],
];

/** C0 は項目スロットではなく段（Lv1〜Lv6）で届くので、N/Aの段を除いて数える。 */
function communicationProgress(state: PatientInterviewState): TaskProgress | null {
  if (!state.slots?.C0) return null;
  const naLevels = new Set(state.naLevelMap?.C0 ?? []);
  const levels = COMMUNICATION_LEVELS.filter(([level]) => !naLevels.has(level));
  if (levels.length === 0) return null;
  const filledLevels = state.slots?.C0?.filledLevels ?? {};
  const got = levels.filter(([level]) => (filledLevels[level] ?? "").trim().length > 0).length;
  return { no: 0, title: "コミュニケーション", got, total: levels.length, done: got >= levels.length };
}

function finish(
  base: Omit<InterviewProgress, "got" | "total" | "complete" | "reachedTarget" | "communication">,
  counted: { got: number; total: number },
  communication: TaskProgress | null,
): InterviewProgress {
  const got = counted.got + (communication?.got ?? 0);
  const total = counted.total + (communication?.total ?? 0);
  return {
    ...base,
    communication,
    got,
    total,
    complete: total > 0 && got >= total,
    reachedTarget: total > 0 && got / total >= PROGRESS_TARGET_RATIO,
  };
}

interface ProgressItem {
  id: string;
  categoryId: string;
  slot: DentistItemSlot;
}

function canonicalCategoryId(categoryId: string): string {
  const normalized = categoryId.trim().toUpperCase();
  return normalized.startsWith("SRC_") ? normalized.slice(4) : normalized;
}

function isCaptured(slot: DentistItemSlot): boolean {
  return slot.filled && !!slot.value && slot.value !== "N/A";
}

/** 採点対象になりうる項目だけを集める（N/Aの段と任意項目を除く）。 */
function collectItems(state: PatientInterviewState): ProgressItem[] {
  const items: ProgressItem[] = [];
  for (const [categoryId, categoryItems] of Object.entries(state.itemSlots ?? {})) {
    if (categoryId === "C0") continue;
    const naLevels = new Set(state.naLevelMap?.[categoryId] ?? []);
    if (state.naCategories?.includes(categoryId)) continue;
    for (const [itemKey, slot] of Object.entries(categoryItems ?? {})) {
      if (!slot || slot.required === false) continue;
      if (naLevels.has(slot.level)) continue;
      items.push({ id: `${categoryId}:${itemKey}`, categoryId: canonicalCategoryId(categoryId), slot });
    }
  }
  return collapseAnyOfGroups(items);
}

/**
 * 採点と同じく、scoringPolicy が anyOf の項目（歯科衛生士提案①②③・患者受容①②③など）は
 * groupKey ごとに「どれか1つで1点」の1項目にまとめる（PatientScoringEngine と同じ規則）。
 */
function collapseAnyOfGroups(items: ProgressItem[]): ProgressItem[] {
  const result: ProgressItem[] = [];
  const groups = new Map<string, ProgressItem[]>();
  for (const item of items) {
    if (item.slot.scoringPolicy !== "anyOf") {
      result.push(item);
      continue;
    }
    const key = `${item.categoryId}:${item.slot.groupKey || item.slot.level}`;
    const group = groups.get(key);
    if (group) {
      group.push(item);
    } else {
      groups.set(key, [item]);
      // 並び順を保つため、最初に出てきた位置に代表を置く
      result.push({ ...item, id: `group:${key}` });
    }
  }
  return result.map((item) => {
    if (!item.id.startsWith("group:")) return item;
    const members = groups.get(item.id.slice("group:".length)) ?? [item];
    const captured = members.find((member) => isCaptured(member.slot));
    return { ...item, slot: (captured ?? members[0]).slot };
  });
}

/** 課題文のタスクと採点項目を結ぶためのキー。 */
export interface TaskMatchKey {
  categoryId: string;
  itemKey: string;
  level: string;
  columnKey?: string;
}

function matchesCell(item: TaskMatchKey, cell: string): boolean {
  const separator = cell.indexOf(":");
  if (separator <= 0) return false;
  if (canonicalCategoryId(cell.slice(0, separator)) !== canonicalCategoryId(item.categoryId)) return false;
  const key = cell.slice(separator + 1).trim();
  return (
    key === item.columnKey ||
    key === item.itemKey ||
    key === item.level ||
    // "Lv2" の指定は "Lv2_frequency" などの細分を含む
    item.level.startsWith(`${key}_`)
  );
}

/**
 * タスクに属する項目を選ぶ。source_cells があればそれで結び、結べなければ related_conditions のカテゴリ全体。
 * 面接中のゲージと評価画面のふりかえりで同じ規則を使う。
 */
export function selectTaskItems<T>(
  task: ProgressSourceTask,
  items: T[],
  keyOf: (item: T) => TaskMatchKey,
): T[] {
  const cells = (task.source_cells ?? []).map((cell) => cell.trim()).filter(Boolean);
  if (cells.length > 0) {
    const matched = items.filter((item) => cells.some((cell) => matchesCell(keyOf(item), cell)));
    if (matched.length > 0) return matched;
  }
  const categories = new Set((task.related_conditions ?? []).map(canonicalCategoryId));
  categories.delete("C0");
  return items.filter((item) => categories.has(canonicalCategoryId(keyOf(item).categoryId)));
}

function itemsForTask(task: ProgressSourceTask, items: ProgressItem[]): ProgressItem[] {
  return selectTaskItems(task, items, (item) => ({
    categoryId: item.categoryId,
    itemKey: item.slot.itemKey,
    level: item.slot.level,
    columnKey: item.slot.columnKey,
  }));
}

function summarize(items: ProgressItem[]): { got: number; total: number } {
  return { got: items.filter((item) => isCaptured(item.slot)).length, total: items.length };
}

/**
 * 面接中の聞き取り状況を、課題文のタスクごとに数える。
 * タスクと項目を結べないときは、対象カテゴリ全体の1本にまとめる。数える項目がなければ null。
 */
export function computeInterviewProgress(
  state: PatientInterviewState | null | undefined,
  tasks: ProgressSourceTask[] | null | undefined,
  targetCategories?: string[] | null,
): InterviewProgress | null {
  if (!state?.itemSlots) return null;
  const items = collectItems(state);
  if (items.length === 0) return null;
  const communication = communicationProgress(state);

  const taskItems = (tasks ?? []).map((task) => itemsForTask(task, items));
  if (taskItems.some((list) => list.length > 0)) {
    const progressTasks = (tasks ?? []).map((task, index) => {
      const { got, total } = summarize(taskItems[index]);
      return {
        no: task.no ?? index + 1,
        title: task.title,
        got,
        total,
        done: total > 0 && got >= total,
      };
    });
    // 複数タスクに属する項目は全体では1回だけ数える
    const union = new Map<string, ProgressItem>();
    taskItems.flat().forEach((item) => union.set(item.id, item));
    const overall = summarize(Array.from(union.values()));
    const countable = progressTasks.filter((task) => task.total > 0);
    const doneTasks = countable.filter((task) => task.done).length;
    return finish(
      { mode: "tasks", tasks: progressTasks, doneTasks, countableTasks: countable.length },
      overall,
      communication,
    );
  }

  const targets = new Set((targetCategories ?? state.targetCategories ?? []).map(canonicalCategoryId));
  targets.delete("C0");
  const scoped = targets.size > 0 ? items.filter((item) => targets.has(item.categoryId)) : items;
  if (scoped.length === 0) return null;
  return finish(
    { mode: "overall", tasks: [], doneTasks: 0, countableTasks: 0 },
    summarize(scoped),
    communication,
  );
}

/** 直前の状態と比べて、新しく聞けた項目があったタスク（最後のもの）と、新しく達成したタスクを返す。 */
export function diffInterviewProgress(
  previous: InterviewProgress | null,
  next: InterviewProgress | null,
): { gained: number; gainedTask: TaskProgress | null; completedTaskNos: number[] } {
  if (!previous || !next) return { gained: 0, gainedTask: null, completedTaskNos: [] };
  const gained = Math.max(0, next.got - previous.got);
  let gainedTask: TaskProgress | null = null;
  const completedTaskNos: number[] = [];
  if (next.communication && previous.communication && next.communication.got > previous.communication.got) {
    gainedTask = next.communication;
  }
  next.tasks.forEach((task, index) => {
    const before = previous.tasks[index];
    if (!before) return;
    if (task.got > before.got) gainedTask = task;
    if (task.done && !before.done) completedTaskNos.push(task.no);
  });
  return { gained, gainedTask, completedTaskNos };
}

/** /api/current-interview/live-score の1項目（採点と同じ計算の点数）。 */
export interface LiveScorePart {
  value: number | null;
  total: number | null;
  max: number | null;
}

/** 報告の採点項目ごとの状態。 */
export interface LiveReportItem {
  categoryId: string;
  itemKey: string;
  level: string;
  columnKey?: string;
  reported: boolean;
  probed: boolean;
}

export interface LiveScore {
  patient: LiveScorePart;
  dentist: LiveScorePart & { skipped: boolean };
  overall: LiveScorePart;
  reportItems?: LiveReportItem[];
}

/** 点数として使える（満点が正の）ときだけ true。 */
export function isScoredPart(part: LiveScorePart | null | undefined): part is LiveScorePart & { total: number; max: number } {
  return typeof part?.total === "number" && typeof part?.max === "number" && part.max > 0;
}

/**
 * ゲージの全体量を、採点と同じ計算の患者面接の点数に置き換える。
 * タスクごとの内訳（課題文の画面用）は画面側の数え方のまま残す。
 */
export function applyLivePatientScore(
  progress: InterviewProgress | null,
  part: LiveScorePart | null | undefined,
): InterviewProgress | null {
  if (!progress || !isScoredPart(part)) return progress;
  const got = Math.round(part.total * 10) / 10;
  const total = part.max;
  return {
    ...progress,
    got,
    total,
    complete: got >= total,
    reachedTarget: got / total >= PROGRESS_TARGET_RATIO,
  };
}

/**
 * 報告の採点項目を、患者面接と同じ数え方で課題文のタスクへ振り分ける。
 * 全体量は報告の点数（聞き返しの減点込み）に置き換える。
 */
export function computeReportProgress(
  liveScore: LiveScore | null | undefined,
  tasks: ProgressSourceTask[] | null | undefined,
  targetCategories?: string[] | null,
): InterviewProgress | null {
  const items = liveScore?.reportItems ?? [];
  if (items.length === 0) return null;
  const itemSlots: NonNullable<PatientInterviewState["itemSlots"]> = {};
  const c0Levels = new Set<string>();
  const c0Filled: Record<string, string> = {};
  for (const item of items) {
    if (item.categoryId === "C0") {
      c0Levels.add(item.level);
      if (item.reported) c0Filled[item.level] = "報告済み";
      continue;
    }
    (itemSlots[item.categoryId] ??= {})[item.itemKey] = {
      itemKey: item.itemKey,
      columnKey: item.columnKey ?? item.level,
      dimension: "",
      label: item.itemKey,
      text: "",
      level: item.level,
      filled: item.reported,
      value: item.reported ? "報告済み" : null,
      required: true,
    } as DentistItemSlot;
  }
  const state: PatientInterviewState = {
    slots: c0Levels.size > 0
      ? { C0: { categoryId: "C0", category: "コミュニケーション", filledLevels: c0Filled, filled: Object.keys(c0Filled).length > 0 } }
      : {},
    itemSlots,
    turnCount: 0,
    naLevelMap: { C0: COMMUNICATION_LEVELS.map(([level]) => level).filter((level) => !c0Levels.has(level)) },
  };
  return applyLivePatientScore(computeInterviewProgress(state, tasks, targetCategories), liveScore?.dentist);
}
